import { getIn } from 'formik';
import React from 'react';
import * as yup from 'yup';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';

import { FormContext } from './FormContext';

export interface FormGroupProps<TItem = Record<string, unknown>> {
  name: string;
  label?: string;
  initialValue?: TItem[];
  minRows?: number;
  maxRows?: number;
  disableAddButton?: boolean;
  disableDeleteButton?: boolean;
  itemGridSize?: number;
  itemGridSpacing?: number;
  validation?: yup.AnySchema;
  changeListener?: (currentValue: TItem[]) => void;
  serializeFunc?: (value: unknown) => unknown;
  deserializeFunc?: (value: unknown) => unknown;
  children?: React.ReactNode;
}

type State = {
  validationSchemaChildren: Record<string, unknown>;
  prevContextValue: any[] | null;
  removedRows: number[];
};

export class FormGroup extends React.Component<FormGroupProps, State> {
  static contextType = FormContext;
  declare context: React.ContextType<typeof FormContext>;

  constructor(props: FormGroupProps) {
    super(props);
    this.state = {
      validationSchemaChildren: {},
      prevContextValue: null,
      removedRows: [],
    };
  }

  private getContext() {
    if (!this.context) {
      throw new Error('FormGroup must be used within a Form');
    }
    return this.context;
  }
  buildRow = (index: number) => {
    const shouldShowDeleteButton =
      !this.props.disableDeleteButton &&
      (!this.props.minRows || index >= (this.props.minRows as number));
    return (
      <Grid
        key={this.translateToFullIndex(index)}
        item
        xs={this.props.itemGridSize ?? 12}
      >
        <Grid
          container
          spacing={this.props.itemGridSpacing ?? 1}
          sx={{ width: '100%' }}
        >
          <FormContext.Provider value={this.getFormContext(index)}>
            {this.props.children}
          </FormContext.Provider>
          <Grid item container xs='auto' alignItems='flex-start'>
            {shouldShowDeleteButton ? (
              <IconButton
                sx={{ p: 0, mt: '22px' }}
                size='small'
                onClick={() => this.removeRow(index)}
              >
                <DeleteIcon />
              </IconButton>
            ) : (
              !this.props.disableDeleteButton && <Grid sx={{ px: '12px' }} />
            )}
          </Grid>
        </Grid>
      </Grid>
    );
  };

  translateToFullIndex = (index: number) => {
    const removedRows = this.state.removedRows;
    for (const removedIndex of removedRows) {
      if (removedIndex <= index) index = index + 1;
    }
    return index;
  };

  removeRow = (index?: number) => {
    const ctx = this.getContext();
    const value = getIn(ctx.values, this.props.name) as any[];
    if (index == null) index = value.length - 1;

    let valueFiltered = value.filter((_, i) => i != index);

    if (this.isEmpty()) valueFiltered = [...valueFiltered, {}];

    ctx.setFieldValue(this.props.name, valueFiltered);
    this.setState({
      removedRows: [
        ...this.state.removedRows,
        this.translateToFullIndex(index),
      ],
    });
  };

  addRow = () => {
    if (this.isFull()) return;

    const ctx = this.getContext();
    let newValues;
    if (!ctx.values?.[this.props.name]) newValues = [{}];
    else newValues = [...(ctx.values as Record<string, unknown[]>)[this.props.name], {}];
    ctx.setFieldValue(this.props.name, newValues);
  };

  isFull = () => {
    const ctx = this.getContext();
    return (
      this.props.maxRows &&
      (getIn(ctx.values, this.props.name)?.length as number) >=
        (this.props.maxRows as number)
    );
  };

  isFilled = () => {
    const ctx = this.getContext();
    return (getIn(ctx.values, this.props.name) as any[])?.every(
      (elem) => JSON.stringify(elem) != '{}',
    );
  };

  isEmpty = () => {
    const ctx = this.getContext();
    return (
      this.props.minRows &&
      (getIn(ctx.values, this.props.name)?.length as number) <=
        (this.props.minRows as number)
    );
  };

  getFormContext = (index: number) => {
    const ctx = this.getContext();
    const prefix = `${this.props.name}[${index}]`;
    return {
      formGroupIndex: index,
      values: getIn(ctx.values, this.props.name)?.[index],
      errors: getIn(ctx.errors, this.props.name)?.[index],
      touched: getIn(ctx.touched, this.props.name)?.[index],
      initialValues: getIn(ctx.initialValues, this.props.name)?.[
        this.translateToFullIndex(index)
      ],
      validate: ctx.validate,
      isSubmitting: ctx.isSubmitting,
      isValid: ctx.isValid,
      setFieldValue: (name: string, value: unknown) => ctx.setFieldValue(`${prefix}.${name}`, value),
      setFieldTouched: (name: string, isTouched?: boolean, shouldValidate?: boolean) => ctx.setFieldTouched(`${prefix}.${name}`, isTouched, shouldValidate),
      setCustomError: (name: string, error: string | null) => ctx.setCustomError(`${prefix}.${name}`, error),
      setInitialValue: (name: string, value: unknown) => ctx.setInitialValue(`${prefix}.${name}`, value),
      setSerializeFunc: (name: string, fn?: (v: unknown) => unknown) => ctx.setSerializeFunc(`${prefix}.${name}`, fn),
      setDeserializeFunc: (name: string, fn?: (v: unknown) => unknown) => ctx.setDeserializeFunc(`${prefix}.${name}`, fn),
      setFieldStepMap: (name: string, stepIndex: number) => ctx.setFieldStepMap(`${prefix}.${name}`, stepIndex),
      setCallbackFunc: (
        listenerField: string,
        dependentField: string,
        callbackFunc: (a: unknown, b: unknown, c: unknown, d?: unknown) => unknown,
      ) => {
        if (dependentField.startsWith('thisGroup.'))
          dependentField = prefix + dependentField.slice('thisGroup.'.length);
        ctx.setCallbackFunc(
          `${prefix}.${listenerField}`,
          dependentField,
          callbackFunc,
        );
      },
      setValidationSchema: (name: string, schema: unknown) => {
        this.setState(
          (state) => {
            if (schema == null) {
              delete (state.validationSchemaChildren as Record<string, unknown>)[name];
            }
            (state.validationSchemaChildren as Record<string, unknown>)[name] = schema;
            return state;
          },
          () => {
            ctx.setValidationSchema(
              this.props.name,
              this.props.validation
                ? (
                    this.props.validation as yup.ArraySchema<
                      unknown[],
                      yup.AnyObject,
                      undefined,
                      ''
                    >
                  ).of(
                    yup
                      .object()
                      .shape(this.state.validationSchemaChildren as Record<string, yup.AnySchema>),
                  )
                : yup
                    .array()
                    .of(
                      yup
                        .object()
                        .shape(this.state.validationSchemaChildren as Record<string, yup.AnySchema>),
                    ),
            );
          },
        );
      },
    } as any;
  };

  getError = () => {
    const ctx = this.getContext();
    return (
      Boolean((ctx.touched as Record<string, unknown>)[this.props.name]) &&
      typeof getIn(ctx.errors, this.props.name) == 'string'
    );
  };

  getHelperText = () => {
    const ctx = this.getContext();
    return this.getError()
      ? (getIn(ctx.errors, this.props.name) as string)
      : '';
  };

  componentDidMount() {
    const ctx = this.getContext();
    this.updateInitialValue();
    this.updateChangeListener();
    if (this.props.serializeFunc)
      ctx.setSerializeFunc(this.props.name, this.props.serializeFunc);
    if (this.props.deserializeFunc)
      ctx.setDeserializeFunc(
        this.props.name,
        this.props.deserializeFunc,
      );
  }

  componentDidUpdate(prevProps: Readonly<FormGroupProps>) {
    if (
      JSON.stringify(this.props.initialValue) !==
      JSON.stringify(prevProps.initialValue)
    ) {
      this.updateInitialValue();
    }
    this.updateChangeListener();
  }

  updateInitialValue = () => {
    const ctx = this.getContext();
    let values: Record<string, unknown>[];
    if (!this.props.initialValue || !this.props.initialValue.length)
      values = [{}];
    else values = this.props.initialValue as Record<string, unknown>[];

    if (this.props.minRows && values.length < this.props.minRows)
      values = [
        ...values,
        ...Array((this.props.minRows as number) - values.length).fill({}),
      ];
    if (this.props.maxRows && values.length > this.props.maxRows)
      values = values.slice(0, this.props.maxRows);

    ctx.setInitialValue(this.props.name, values);
    ctx.setFieldValue(this.props.name, values);
  };

  updateChangeListener = () => {
    const ctx = this.getContext();
    if (
      this.props.changeListener &&
      (getIn(ctx.values, this.props.name)?.length as number) !=
        this.state.prevContextValue?.length
    )
      this.props.changeListener(getIn(ctx.values, this.props.name));
    if (
      JSON.stringify(getIn(ctx.values, this.props.name)) !==
      JSON.stringify(this.state.prevContextValue)
    )
      this.setState({
        prevContextValue: getIn(ctx.values, this.props.name),
      });
  };

  // Isso é necessário para garantir que a validação rode depois que um
  // componente é desmontado. Um pouco gambiarra (devido ao strict mode
  // também), mas foi o que deu pra fazer.
  componentWillUnmount() {
    const ctx = this.getContext();
    ctx.setInitialValue(this.props.name, undefined);
    ctx.setValidationSchema(this.props.name, undefined);
    ctx.setFieldValue(this.props.name, undefined);
    setTimeout(() => ctx.validate(), 300);
  }

  render() {
    const ctx = this.getContext();
    let inputs: React.ReactNode;
    const values = getIn(ctx.values, this.props.name) as unknown[];
    const currentGroupSize = values?.length ?? 0;
    if (currentGroupSize) {
      inputs = values.map((_elem, index) => this.buildRow(index));
    }
    const shouldShowAddButton = !this.props.disableAddButton && !this.isFull();

    return (
      <>
        {this.props.label ? (
          <FormLabel sx={{ mb: '5px' }}>{this.props.label}</FormLabel>
        ) : null}
        <Grid container>{inputs}</Grid>
        <Grid container>
          <Grid item xs>
            {this.getError() && (
              <FormHelperText error>{this.getHelperText()}</FormHelperText>
            )}
          </Grid>
          {shouldShowAddButton ? (
            <Grid item xs='auto'>
              <IconButton
                sx={{ p: 0, mt: '10px' }}
                size='small'
                onClick={() => this.addRow()}
              >
                <AddIcon />
              </IconButton>
            </Grid>
          ) : null}
        </Grid>
      </>
    );
  }
}
